import React, { createContext, useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ToastBadge {
  text: string;
  color?: string;
}

export interface ShowToastConfig {
  message: string;
  badge?: ToastBadge;
  duration?: number;
}

interface ToastItem extends ShowToastConfig {
  id: string;
  leaving: boolean;
}

interface ToastContextValue {
  showToast: (config: ShowToastConfig) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

export const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

// ─── ToastItemView ────────────────────────────────────────────────────────────

const LEAVE_MS = 300;

const ToastItemView: React.FC<{ item: ToastItem }> = ({ item }) => {
  const [entered, setEntered] = useState(false);

  React.useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const visible = entered && !item.leaving;

  return (
    <div
      className='flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-[13px] font-medium text-white pointer-events-auto'
      style={{
        backgroundColor: '#1BD2A4',
        transform: visible ? 'translateY(0)' : 'translateY(calc(100% + 24px))',
        opacity: visible ? 1 : 0,
        transition: `transform ${LEAVE_MS}ms ease, opacity ${LEAVE_MS}ms ease`,
      }}
    >
      <span className='text-[15px] text-white'>✓</span>
      <span className='text-white'>{item.message}</span>
      {item.badge && (
        <span
          className='flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-semibold text-white whitespace-nowrap'
          style={{ backgroundColor: item.badge.color ?? '#F97316' }}
        >
          • {item.badge.text}
        </span>
      )}
    </div>
  );
};

// ─── ToastContainer ───────────────────────────────────────────────────────────

const ToastContainer: React.FC<{ items: ToastItem[] }> = ({ items }) => {
  if (items.length === 0) return null;
  return ReactDOM.createPortal(
    <div className='fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 items-center pointer-events-none'>
      {items.map(item => <ToastItemView key={item.id} item={item} />)}
    </div>,
    document.body,
  );
};

// ─── ToastProvider ────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const showToast = useCallback((config: ShowToastConfig) => {
    const id = `toast-${Date.now()}`;
    const duration = config.duration ?? 3000;

    setItems(prev => [...prev, { ...config, id, leaving: false }]);

    timersRef.current[id] = setTimeout(() => {
      setItems(prev => prev.map(t => t.id === id ? { ...t, leaving: true } : t));
      setTimeout(() => {
        setItems(prev => prev.filter(t => t.id !== id));
        delete timersRef.current[id];
      }, LEAVE_MS);
    }, duration);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer items={items} />
    </ToastContext.Provider>
  );
}
